import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import type { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import type { LoginValues } from '../loginSchema';
import { Field, Tick } from './LoginBits';

// ────────────────────────────────────────────────────────────────────────────
// LoginCard · el modal real con form de Supabase, ticks en esquinas, glass.
// ────────────────────────────────────────────────────────────────────────────

type LoginCardProps = {
  form: ReturnType<typeof useForm<LoginValues>>;
  onSubmit: (values: LoginValues) => void | Promise<void>;
  submitting: boolean;
  visible: boolean;
  isMobile?: boolean;
  /** Si viene, reemplaza al formulario de email y contraseña (entrada rápida). */
  quickSlot?: React.ReactNode;
  /** El email viene precargado desde un ingreso anterior en este navegador. */
  recordado?: boolean;
  onNoSoyYo?: () => void;
};

export function LoginCard({
  form,
  onSubmit,
  submitting,
  visible,
  isMobile,
  quickSlot,
  recordado,
  onNoSoyYo,
}: LoginCardProps) {
  const emailError = form.formState.errors.email?.message;
  const passwordError = form.formState.errors.password?.message;
  const [showPassword, setShowPassword] = useState(false);

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      noValidate
      style={{
        width: '100%',
        maxWidth: '100%',
        padding: isMobile ? 22 : 26,
        background: 'rgba(10, 16, 21, 0.86)',
        border: '1px solid rgba(102, 224, 255, 0.28)',
        borderRadius: 8,
        backdropFilter: 'blur(16px) saturate(140%)',
        WebkitBackdropFilter: 'blur(16px) saturate(140%)',
        boxShadow: `
          0 0 0 1px rgba(102, 224, 255, 0.06),
          0 32px 80px -28px rgba(0,0,0,0.9),
          0 0 80px -20px rgba(102, 224, 255, 0.18),
          inset 0 1px 0 rgba(255,255,255,0.04)
        `,
        position: 'relative',
        opacity: visible ? 1 : 0,
        transform: visible
          ? 'translateY(0) scale(1)'
          : 'translateY(18px) scale(0.97)',
        transition:
          'opacity .55s cubic-bezier(.22,1,.36,1), transform .55s cubic-bezier(.22,1,.36,1)',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <Tick pos="tl" />
      <Tick pos="tr" />
      <Tick pos="bl" />
      <Tick pos="br" />

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#66e0ff',
              boxShadow: '0 0 6px #66e0ff',
            }}
          />
          <span
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 10,
              letterSpacing: '0.18em',
              color: 'rgba(102, 224, 255, 0.85)',
              textTransform: 'uppercase',
            }}
          >
            Secure terminal
          </span>
        </div>
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 9.5,
            letterSpacing: '0.12em',
            color: 'rgba(255,255,255,0.4)',
            textTransform: 'uppercase',
          }}
        >
          /auth · v2
        </span>
      </div>

      <h2
        style={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: isMobile ? 21 : 22,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: '#f6fbff',
          margin: '6px 0 4px',
        }}
      >
        Iniciar sesión
      </h2>
      <p
        style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.55)',
          margin: '0 0 18px',
          lineHeight: 1.5,
        }}
      >
        Acceso al panel operativo de DenDev.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {quickSlot ?? (
          <>
        <Field
          label="Email"
          error={emailError}
          hint={
            recordado && onNoSoyYo ? (
              <button
                type="button"
                className="dd-link-btn"
                onClick={onNoSoyYo}
                disabled={submitting}
              >
                No soy yo
              </button>
            ) : undefined
          }
        >
          <input
            type="email"
            autoComplete="email"
            autoFocus={!recordado}
            placeholder="tu@dendev.ar"
            disabled={submitting}
            {...form.register('email')}
          />
        </Field>

        <Field
          label="Contraseña"
          error={passwordError}
          hint={
            <Link to="/auth/reset-password" className="dd-forgot-link">
              ¿Olvidaste?
            </Link>
          }
          trailing={
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={
                showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
              }
              aria-pressed={showPassword}
              title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 2,
                margin: 0,
                cursor: 'pointer',
                color: 'rgba(102, 224, 255, 0.6)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                lineHeight: 0,
              }}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          }
        >
          <input
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            autoFocus={recordado}
            placeholder="••••••••"
            disabled={submitting}
            {...form.register('password')}
          />
        </Field>

        <button
          type="submit"
          disabled={submitting}
          className="dd-enter-btn"
          aria-busy={submitting}
        >
          {submitting ? (
            <>
              <span className="dd-spinner" aria-hidden="true" />
              <span>Verificando…</span>
            </>
          ) : (
            <>
              <span>Entrar</span>
              <span aria-hidden="true" style={{ fontSize: 14 }}>
                →
              </span>
            </>
          )}
        </button>
          </>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginTop: 4,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 9.5,
            letterSpacing: '0.16em',
            color: 'rgba(255,255,255,0.4)',
            textTransform: 'uppercase',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 4,
              height: 4,
              borderRadius: '50%',
              background: '#3ddc97',
              boxShadow: '0 0 6px #3ddc97',
            }}
          />
          <span>sesión segura · verificación en dos pasos</span>
        </div>
      </div>
    </form>
  );
}
